import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  User, 
  Building2, 
  Monitor, 
  Clock, 
  MessageSquare, 
  Send,
  Eye,
  EyeOff,
  Download,
  FileText
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TicketSLAIndicator } from './TicketSLAIndicator';
import { TicketStatusManager } from './TicketStatusManager';
import { TicketAssignmentManager } from './TicketAssignmentManager';
import { useSupportTicket, useAddTicketMessage, useUpdateSupportTicket } from '@/hooks/use-support-tickets';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { ImageViewerModal } from '../ImageViewerModal';

interface TicketDetailModalProps {
  ticketId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TicketDetailModal({ ticketId, open, onOpenChange }: TicketDetailModalProps) {
  const [newMessage, setNewMessage] = useState('');
  const [isInternalMessage, setIsInternalMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageAttachments, setImageAttachments] = useState<Array<{id: string, name: string, signedUrl: string}>>([]);

  const { data: ticket, isLoading } = useSupportTicket(ticketId);
  const addMessage = useAddTicketMessage();
  const updateTicket = useUpdateSupportTicket(ticketId);
  const { toast } = useToast();

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setIsSubmitting(true);
    try {
      await addMessage.mutateAsync({
        ticket_id: ticketId,
        message: newMessage,
        is_internal: isInternalMessage,
      });

      setNewMessage('');
      toast({
        title: 'Mensagem enviada',
        description: isInternalMessage ? 'Mensagem interna adicionada' : 'Resposta enviada ao usuário',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar a mensagem',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async (status: 'open' | 'in_progress' | 'resolved' | 'closed' | 'paused', reason?: string) => {
    try {
      await updateTicket.mutateAsync({
        status,
        paused_reason: reason,
      });

      toast({
        title: 'Status atualizado',
        description: `Chamado ${status === 'paused' ? 'pausado' : 'atualizado'} com sucesso`,
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o status',
        variant: 'destructive',
      });
    }
  };

  const handleAssignmentUpdate = async (assigneeId: string | null) => {
    try {
      await updateTicket.mutateAsync({
        assigned_to: assigneeId,
      });

      toast({
        title: 'Atribuição atualizada',
        description: assigneeId ? 'Responsável atribuído' : 'Atribuição removida',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar a atribuição',
        variant: 'destructive',
      });
    }
  };

  const downloadAttachment = async (attachment: any) => {
    try {
      const { data, error } = await supabase.storage
        .from('support-attachments')
        .download(attachment.path);

      if (error) {
        console.error('Storage download error:', error);
        throw error;
      }

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Sucesso",
        description: "Anexo baixado com sucesso",
      });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast({
        title: "Erro",
        description: "Erro ao baixar anexo. Verifique se o arquivo ainda existe.",
        variant: "destructive",
      });
    }
  };

  // Componente para preview de anexos
  const AttachmentPreview = ({ attachment, onDownload }: { attachment: any; onDownload: (attachment: any) => void }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoadingUrl, setIsLoadingUrl] = useState(false);
    const isImage = attachment.type?.startsWith('image/');
    
    useEffect(() => {
      if (isImage) {
        const getSignedUrl = async () => {
          setIsLoadingUrl(true);
          try {
            const { data, error } = await supabase.storage
              .from('support-attachments')
              .createSignedUrl(attachment.path, 3600);
            
            if (error) {
              console.error('Error creating signed URL:', error);
            } else if (data?.signedUrl) {
              setImageUrl(data.signedUrl);
            }
          } catch (error) {
            console.error('Error creating signed URL:', error);
          } finally {
            setIsLoadingUrl(false);
          }
        };
        
        getSignedUrl();
      }
    }, [attachment.path, isImage]);

    const handleImageClick = async () => {
      if (!isImage || !imageUrl) return;

      // Get all image attachments from the ticket
      const allAttachments = ticket?.attachments || [];
      const imageAttachments = allAttachments.filter((att: any) => att.type?.startsWith('image/'));
      
      // Create signed URLs for all images
      const imagesWithUrls = await Promise.all(
        imageAttachments.map(async (att: any) => {
          const { data } = await supabase.storage
            .from('support-attachments')
            .createSignedUrl(att.path, 3600);
          
          return {
            id: att.id,
            name: att.name,
            signedUrl: data?.signedUrl || ''
          };
        })
      );

      const currentIndex = imagesWithUrls.findIndex(img => img.id === attachment.id);
      
      setImageAttachments(imagesWithUrls.filter(img => img.signedUrl));
      setSelectedImageIndex(Math.max(0, currentIndex));
      setImageViewerOpen(true);
    };
    
    if (isImage) {
      if (isLoadingUrl) {
        return (
          <div className="border rounded-lg p-3">
            <div className="w-full h-32 bg-muted animate-pulse rounded mb-2"></div>
            <div className="flex items-center justify-between">
              <span className="text-sm truncate">{attachment.name}</span>
              <span className="text-xs text-muted-foreground">Carregando...</span>
            </div>
          </div>
        );
      }

      if (imageUrl) {
        return (
          <div className="border rounded-lg p-2">
            <img 
              src={imageUrl} 
              alt={attachment.name}
              className="w-full h-32 object-cover rounded cursor-pointer mb-2 hover:opacity-80 transition-opacity"
              onClick={handleImageClick}
              title="Clique para visualizar"
            />
            <div className="flex items-center justify-between">
              <span className="text-sm truncate">{attachment.name}</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => onDownload(attachment)}
                title="Baixar arquivo"
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );
      }
      
      // Fallback for images that failed to load
      return (
        <div className="border rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm truncate">{attachment.name}</span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onDownload(attachment)}
          >
            <Download className="h-4 w-4 mr-1" />
            Baixar
          </Button>
        </div>
      );
    }
    
    return (
      <div className="border rounded-lg p-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <span className="text-sm truncate">{attachment.name}</span>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onDownload(attachment)}
        >
          <Download className="h-4 w-4 mr-1" />
          Baixar
        </Button>
      </div>
    );
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getPriorityLabel = (priority: string) => {
    const priorities = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      urgent: 'Urgente',
    };
    return priorities[priority as keyof typeof priorities] || priority;
  };

  const getPriorityVariant = (priority: string) => {
    const variants = {
      low: 'secondary',
      medium: 'default',
      high: 'secondary',
      urgent: 'destructive',
    };
    return variants[priority as keyof typeof variants] || 'outline';
  };

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl h-[80vh]">
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!ticket) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="text-center py-8">
            <p className="text-muted-foreground">Chamado não encontrado</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl h-[80vh] p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-3">
            <span>Chamado #{ticket.ticket_number ? String(ticket.ticket_number).padStart(6, '0') : '—'}</span>
            <TicketSLAIndicator
              sla_due_at={ticket.sla_due_at}
              status={ticket.status}
              priority={ticket.priority}
              created_at={ticket.created_at}
            />
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-12 gap-0 overflow-hidden">
          {/* User/Company/System Info - Left Column */}
          <div className="col-span-3 border-r bg-muted/20 p-4 overflow-y-auto">
            <div className="space-y-4">
              {/* User Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Usuário
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {getInitials((((ticket as any).user_profile as any)?.full_name || 'U'))}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{(((ticket as any).user_profile as any)?.full_name)}</p>
                      <p className="text-xs text-muted-foreground truncate">{(((ticket as any).user_profile as any)?.email)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Company Info */}
              {(ticket.company as any)?.name && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Empresa
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm font-medium">{(ticket.company as any).name}</p>
                  </CardContent>
                </Card>
              )}

              {/* System Info */}
              {(ticket.system as any)?.name && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Monitor className="h-4 w-4" />
                      Sistema
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm font-medium">{(ticket.system as any).name}</p>
                  </CardContent>
                </Card>
              )}

              {/* Ticket Details */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Detalhes
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Categoria</Label>
                    <p className="text-sm">{(ticket.category as any)?.name || 'Sem categoria'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Prioridade</Label>
                    <div className="mt-1">
                      <Badge variant={getPriorityVariant(ticket.priority) as any}>
                        {getPriorityLabel(ticket.priority)}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Criado em</Label>
                    <p className="text-xs">
                      {format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Attachments Section */}
              {ticket.attachments && ticket.attachments.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Anexos ({ticket.attachments.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-3">
                      {ticket.attachments.map((attachment: any) => (
                        <AttachmentPreview 
                          key={attachment.id} 
                          attachment={attachment} 
                          onDownload={downloadAttachment}
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Messages Timeline - Center Column */}
          <div className="col-span-6 flex flex-col">
            <div className="px-4 py-3 border-b bg-background">
              <h3 className="font-medium">{ticket.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{ticket.description}</p>
            </div>

            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {ticket.support_ticket_messages?.map((message) => (
                  <div
                    key={message.id}
                    className={`flex gap-3 ${
                      message.is_internal ? 'opacity-75' : ''
                    }`}
                  >
                    <Avatar className="h-8 w-8 mt-1">
                      <AvatarFallback className="text-xs">
                        {getInitials((((message as any).sender as any)?.full_name || 'U'))}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{(((message as any).sender as any)?.full_name)}</span>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(message.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                        </span>
                        {message.is_internal && (
                          <Badge variant="outline" className="text-xs">
                            <Eye className="h-3 w-3 mr-1" />
                            Interno
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm bg-muted/50 rounded-lg p-3">
                        <p className="whitespace-pre-wrap">{message.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Message Input */}
            <div className="border-t p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Button
                  variant={isInternalMessage ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIsInternalMessage(!isInternalMessage)}
                  className="gap-2"
                >
                  {isInternalMessage ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {isInternalMessage ? 'Mensagem Interna' : 'Resposta ao Usuário'}
                </Button>
              </div>
              
              <div className="flex gap-2">
                <Textarea
                  placeholder={isInternalMessage ? "Adicionar nota interna..." : "Responder ao usuário..."}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  rows={3}
                  className="flex-1"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || isSubmitting}
                  size="sm"
                  className="self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Actions - Right Column */}
          <div className="col-span-3 border-l bg-muted/20 p-4 overflow-y-auto">
            <div className="space-y-6">
              <TicketStatusManager
                currentStatus={ticket.status}
                ticketId={ticketId}
                onStatusUpdate={handleStatusUpdate}
                disabled={updateTicket.isPending}
              />

              <Separator />

              <TicketAssignmentManager
                currentAssigneeId={ticket.assigned_to}
                ticketId={ticketId}
                onAssignmentUpdate={handleAssignmentUpdate}
                disabled={updateTicket.isPending}
              />
            </div>
          </div>
        </div>
      </DialogContent>
      
      <ImageViewerModal
        images={imageAttachments}
        initialIndex={selectedImageIndex}
        open={imageViewerOpen}
        onOpenChange={setImageViewerOpen}
        onDownload={(attachment) => downloadAttachment({ 
          id: attachment.id, 
          name: attachment.name, 
          file_path: attachment.signedUrl.split('/').pop() || attachment.name 
        })}
      />
    </Dialog>
  );
}